import { createFileRoute, Link } from "@tanstack/react-router";
import {
  LearnArticleLayout,
  learnArticleHead,
} from "@/components/LearnArticleLayout";

const PATH = "/learn/google-ads-management-cost-colorado";
const TITLE =
  "What Does Google Ads Management Cost in Colorado? (2026 Honest Pricing)";
const DESCRIPTION =
  "Real 2026 pricing for Google Ads / PPC management in Colorado. Management fees, ad spend, and what makes a campaign profitable vs. a money pit.";

export const Route = createFileRoute("/learn/google-ads-management-cost-colorado")({
  head: () => learnArticleHead({ title: TITLE, description: DESCRIPTION, path: PATH }),
  component: Article,
});

function Article() {
  return (
    <LearnArticleLayout
      category="Pricing"
      title={TITLE}
      intro="Google Ads in Colorado works — when it's managed by someone who actually knows the platform. Here's what real PPC management costs in 2026, including the ad-spend math most agencies hide."
      readTime="5 min read"
      publishedDate="2026-06-22"
      path={PATH}
      definition={{
        term: "What does Google Ads management cost in Colorado?",
        body: "Most Colorado Google Ads management runs $750–$2,500/month in agency fees, plus $1,500–$25,000/month in ad spend paid directly to Google. At Colorado Web Impressions, PPC management is included in our $2,750/month Expert Marketing package — no separate management fee, ad spend billed direct.",
      }}
      toc={[
        { id: "two-numbers", label: "The two numbers you need to know" },
        { id: "real-management-fees", label: "Real management fees in Colorado" },
        { id: "ad-spend", label: "How much ad spend you actually need" },
        { id: "our-pricing", label: "What we charge and why" },
        { id: "red-flags", label: "Red flags in PPC pricing" },
      ]}
      faqs={[
        {
          q: "Should I pay a percentage of ad spend or a flat fee?",
          a: "Flat fee. Percentage-of-spend models incentivize agencies to push your budget up, not your ROI. A flat fee aligns interest: the agency wins when leads get cheaper, not when ad spend gets bigger.",
        },
        {
          q: "Is Google Ads worth it for a small Colorado business?",
          a: "Yes if your average ticket supports it. Trades with $300+ tickets (HVAC, roofing, plumbing, remodel) almost always profit from PPC. Sub-$100 tickets struggle — Maps and SEO are usually better there.",
        },
        {
          q: "What's the minimum ad spend that makes sense in Colorado?",
          a: "$1,500/month for a single-city service business. Below that, Google's algorithm doesn't have enough data to optimize and CPL stays high. Mid-market spend is $3,000–$8,000; competitive trades (PI law, hail roofing) easily run $15k+.",
        },
        {
          q: "Do you mark up Google Ads spend?",
          a: "No. Ad spend is billed directly by Google to your card or invoice. You see every dollar. We charge for the work, not the spend.",
        },
      ]}
    >
      <h2 id="two-numbers">The two numbers you need to know</h2>
      <p>
        Google Ads has two separate costs that get confused all the time:
      </p>
      <ol>
        <li><strong>Management fee</strong> — what the agency charges to build, run, and tune the campaigns.</li>
        <li><strong>Ad spend</strong> — what Google charges per click, paid directly by you.</li>
      </ol>
      <p>
        A "$500/month PPC service" usually means $500 in fees AND $500 in ad
        spend — not enough of either to produce real results in a Colorado market.
      </p>

      <h2 id="real-management-fees">Real management fees in Colorado</h2>
      <ul>
        <li><strong>$500–$750/month:</strong> Solo freelancer, often single account, limited reporting.</li>
        <li><strong>$1,000–$1,800/month:</strong> Most Colorado small-agency rates. Realistic for $3k–$10k/month ad spend.</li>
        <li><strong>$2,000–$3,500/month:</strong> Larger agencies, multi-location, multi-campaign.</li>
        <li><strong>15–20% of ad spend:</strong> Common enterprise model. Aligns agency to spend, not to ROI.</li>
      </ul>

      <h2 id="ad-spend">How much ad spend you actually need</h2>
      <p>
        Spend by competition level for a single Colorado city:
      </p>
      <ul>
        <li><strong>Low CPC categories</strong> (carpet cleaning, landscaping, handyman): $1,500–$4,000/month.</li>
        <li><strong>Mid CPC</strong> (HVAC, dental, general contractor): $3,000–$8,000/month.</li>
        <li><strong>High CPC</strong> (roofing, criminal defense, family law): $5,000–$15,000/month.</li>
        <li><strong>Extreme CPC</strong> (personal injury, mass tort): $15,000–$60,000+/month.</li>
      </ul>

      <h2 id="our-pricing">What we charge and why</h2>
      <p>
        At Colorado Web Impressions, Google Ads management is included inside
        our <strong>Expert Marketing</strong> package at $2,750/month,
        month-to-month. No separate PPC management fee. Ad spend is billed directly by
        Google so you see every click, every dollar.
      </p>
      <p>
        Or get PPC management as part of{" "}
        <Link to="/pricing" className="text-primary underline">Growth Marketing at $1,650/month</Link>{" "}
        if you already have a site.
      </p>

      <h2 id="red-flags">Red flags in PPC pricing</h2>
      <ul>
        <li><strong>Agencies that own your Google Ads account.</strong> You should own it — period. If you leave, the account leaves with you.</li>
        <li><strong>No conversion tracking setup.</strong> If they can't show you actual phone calls and form fills, they can't optimize the campaign.</li>
        <li><strong>Marked-up ad spend.</strong> Some agencies bill ad spend through themselves and skim. Always pay Google directly.</li>
        <li><strong>"Guaranteed Page 1" PPC claims.</strong> Page 1 in PPC means nothing if your CPL is upside-down.</li>
      </ul>
      <p>
        Related reading:{" "}
        <Link to="/learn/google-ads-vs-seo-colorado" className="text-primary underline">Google Ads vs. SEO in Colorado</Link>,{" "}
        <Link to="/learn/monthly-lead-generation-cost-colorado" className="text-primary underline">monthly lead-gen cost</Link>.
      </p>
    </LearnArticleLayout>
  );
}
